import React, { useEffect, useRef, useState } from "react";
import Department from "../types/Department";
import {
    acceptPermitAsync,
    cancelCounterSignatureRequestAsync,
    createPermitAsync,
    createSignatureAsync,
    deleteBase64SignatureAsync,
    deleteSignatureAsync,
    getDepartmentById,
    getPermitDocumentById,
    getPermitDocumentBySequenceIdentifier,
    getPermitDocumentByWorKPermitId,
    rejectPermitAsync,
    renewPermitAsync,
    sendCounterSignatureRequestAsync,
    updatePermitAsync
} from "../apiClient";
import { scrollToTop, showDangerDialog, showSuccessAlert, showValidationErrorDialog } from "../common";
import Result, { failedResult, successfulResult } from "../types/Result";
import ProblemDetails from "../types/responses/ProblemDetails";
import PositionPickerRow from "../types/forms/Permits/PositionPickerRow";
import { ParticipantListItem } from "../components/Forms/Permits/ParticipantsList";
import PersonFormElement from "../types/forms/PersonFormElement";
import Errors from "../types/responses/Errors";
import { permitPageUrl } from "../common/permitUtils";
import Resources from "../types/Resources";
import { CodeRole, CounterSignatureLocation, PermitType, SignatureType } from "../common/enums";
import CreateSignatureRequest from "../types/requests/CreateSignatureRequest";
import Signature from "../types/Signature";
import CounterSignatureRequestRecipient from "../types/CounterSignatureRequestRecipient";
import CreateCounterSignatureRequestPostRequest from "../types/requests/CreateCounterSignatureRequestPostRequest";
import CounterSignatureRequest from "../types/CounterSignatureRequest";
import { Transformation } from "../types/functions";
import { PermitFormProps } from "../types/forms/Permits/PermitFormProps";
import { PermitDocument, PermitFormState, PermitRequest } from "../types/permits";
import { permitContentVersions } from "../types/WorkPermit";

export type UsePermitFormManagerProps<
    TPermitDocument extends PermitDocument,
    TPermitForm extends PermitFormState,
    TPermitRequest extends PermitRequest
> = PermitFormProps & {
    defaultFormState: () => TPermitForm;
    permitType: PermitType;
    toFormState: (permitDocument: TPermitDocument) => TPermitForm;
    toRequest: (formState: TPermitForm) => TPermitRequest;
    populateErrorMessages: (formState: TPermitForm, errors: Errors) => TPermitForm;
    clearErrorMessages: (formState: TPermitForm) => TPermitForm;
    fetchDataAsync?: () => Promise<void>;
    updateAsync?: (formState: TPermitForm) => Promise<void>;

    //
    // Indicates that no data should be fetched from the backend. This is used for anonymous permits when the user is not logged int.
    //
    disableDataFetch?: boolean;
};

function usePermitForm<
    TPermitDocument extends PermitDocument,
    TPermitForm extends PermitFormState,
    TPermitRequest extends PermitRequest
>(props: UsePermitFormManagerProps<TPermitDocument, TPermitForm, TPermitRequest>) {
    const [storedPermit, setStoredPermit] = useState<TPermitDocument | undefined>();
    const [department, setDepartment] = useState<Department | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [formState, setFormState] = useState(props.defaultFormState());
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showAcceptUnsavedChangesModal, setShowAcceptUnsavedChangesModal] = useState(false);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [showRejectPermitModal, setShowRejectPermitModal] = useState(false);

    useEffect(() => {
        (async () => {
            if (props.disableDataFetch) {
                setIsLoading(false);
                return;
            }

            if (!props.fetchDataAsync) {
                await defaultFetchData();
            } else {
                await props.fetchDataAsync();
            }
            setIsLoading(false);
        })();
    }, []);

    async function defaultFetchData() {
        const promises: Promise<any>[] = [getDepartmentById(props.departmentId)];

        if (props.documentReferenceId) {
            promises.push(getPermitDocumentById<TPermitDocument>(props.documentReferenceId, props.permitType));
        } else if (props.sequenceIdentifier) {
            promises.push(
                getPermitDocumentBySequenceIdentifier<TPermitDocument>(props.sequenceIdentifier, props.permitType)
            );
        } else if (props.workPermitId) {
            promises.push(getPermitDocumentByWorKPermitId<TPermitDocument>(props.workPermitId, props.permitType));
        } else if (props.copyOfDocumentReferenceId) {
            promises.push(getPermitDocumentById<TPermitDocument>(props.copyOfDocumentReferenceId, props.permitType));
        }

        const [getDepartmentResult, getPermitResult] = await Promise.all(promises);

        if (getDepartmentResult.failed) {
            console.error(getDepartmentResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetDepartmentError"));
            setIsLoading(false);
            return;
        }

        if (getPermitResult && getPermitResult.failed) {
            console.error(getPermitResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("GetPermitError"));
            setIsLoading(false);
            return;
        }

        if (getPermitResult && (props.documentReferenceId || props.sequenceIdentifier || props.workPermitId)) {
            setStoredPermit(getPermitResult.value);
            setFormState(props.toFormState(getPermitResult.value));
        } else if (getPermitResult) {
            //
            // getPermitResult.value is the permit with id copyOfDocumentReferenceId
            //
            setFormState(props.toFormState(getPermitResult.value));
        }

        setDepartment(getDepartmentResult.value);

        setIsLoading(false);
    }

    async function acceptPermitHandlerAsync() {
        if (!storedPermit) {
            return;
        }

        const acceptResult: Result<void, ProblemDetails> = await acceptPermitAsync(
            storedPermit.entityId,
            props.permitType
        );

        if (acceptResult.error) {
            console.error(acceptResult);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("AcceptError"));
            return;
        }

        showSuccessAlert(props.resources.get("PermitAccepted"));

        await refreshStoredPermitAsync();
    }

    async function refreshStoredPermitAsync(): Promise<Result<TPermitDocument, ProblemDetails>> {
        if (!storedPermit) {
            console.error("Attempted to refresh the stored permit with no permit in state.");
            return failedResult({
                status: 500,
                title: "No stored permit in state",
                errors: {}
            });
        }

        const storedPermitResult = await getPermitDocumentById<TPermitDocument>(storedPermit.id, props.permitType);

        if (storedPermitResult.failed) {
            console.error(storedPermitResult.error);
            window.location.reload();
            return failedResult(storedPermitResult.error);
        }

        setStoredPermit(storedPermitResult.value);

        return successfulResult(storedPermitResult.value);
    }

    function addPositionPickerRowHandler(newRow: PositionPickerRow) {
        setFormState((prevValue) => {
            //
            // All rows before/above the new row remain unchanged. All rows after/beneath the new row get an increased index.
            //
            const updatedPositionRows = [
                ...prevValue.positions.map((p) => (p.order < newRow.order ? p : { ...p, order: p.order + 1 })),
                newRow
            ].sort((a, b) => a.order - b.order);

            return {
                ...prevValue,
                positions: updatedPositionRows
            };
        });

        setHasUnsavedChanges(true);
    }

    function removePositionPickerRowHandler(removedRow: PositionPickerRow) {
        setFormState((prevValue) => {
            const updatedPositionRows = prevValue.positions
                .filter((p) => p.order !== removedRow.order)
                .map((p) => (p.order < removedRow.order ? p : { ...p, order: p.order - 1 }));

            return {
                ...prevValue,
                positions: updatedPositionRows
            };
        });

        setHasUnsavedChanges(true);
    }

    function updatePositionPickerRowHandler(updatedRow: PositionPickerRow) {
        setFormState((prevValue) => {
            const updatedPositions = prevValue.positions.map((p) => (p.order === updatedRow.order ? updatedRow : p));

            return {
                ...prevValue,
                positions: updatedPositions
            };
        });

        setHasUnsavedChanges(true);
    }

    function addParticipantHandler(newParticipant: ParticipantListItem) {
        if (!(formState as any).persons) {
            return;
        }

        setHasUnsavedChanges(true);

        setFormState((prevValue) => {
            const updatedPersons = (prevValue as any).persons
                .map((p: ParticipantListItem) => (p.index < newParticipant.index ? p : { ...p, index: p.index + 1 }))
                .concat([newParticipant])
                .sort((a: ParticipantListItem, b: ParticipantListItem) => a.index - b.index);

            return { ...prevValue, persons: updatedPersons };
        });
    }

    function removeParticipantHandler(removedParticipant: ParticipantListItem) {
        if (!(formState as any).persons) {
            return;
        }

        setHasUnsavedChanges(true);

        setFormState((prevValue) => {
            const updatedPersons = (prevValue as any).persons
                .filter((p: ParticipantListItem) => p.index !== removedParticipant.index)
                .map((p: ParticipantListItem) =>
                    p.index < removedParticipant.index ? p : { ...p, index: p.index - 1 }
                );

            return { ...prevValue, persons: updatedPersons };
        });
    }

    function updateParticipantHandler(updatedParticipant: ParticipantListItem) {
        if (!(formState as any).persons) {
            return;
        }

        setHasUnsavedChanges(true);

        setFormState((prevValue) => {
            const updatedPersons = (prevValue as any).persons.map((p: ParticipantListItem) =>
                p.index === updatedParticipant.index ? updatedParticipant : p
            );

            return { ...prevValue, persons: updatedPersons };
        });
    }

    function formStateChangeHandler(key: keyof TPermitForm, value: any) {
        setHasUnsavedChanges(true);

        setFormState((prevValue) => ({ ...prevValue, [key]: { ...prevValue[key], value: value } }));
    }

    function personFormElementChangeHandler(key: keyof TPermitForm, personFormElement: PersonFormElement) {
        setHasUnsavedChanges(true);

        setFormState((prevValue) => ({ ...prevValue, [key]: personFormElement }));
    }

    async function submitCreateHandlerAsync(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const permitRequest = props.toRequest(formState);
        await createHandlerAsync(permitRequest);
    }

    async function createHandlerAsync(
        permitRequest: TPermitRequest,
        eventHandlers?: {
            onSuccess?: () => void;
            onError?: (error: ProblemDetails) => void;
        }
    ) {
        setRequestInProgress(true);
        const result = await createPermitAsync(permitRequest, props.permitType);
        setRequestInProgress(false);

        if (result.failed) {
            console.error(result.error);

            if (!!eventHandlers && !!eventHandlers.onError) {
                eventHandlers.onError(result.error);
                return;
            }

            if (!result.error.errors) {
                showDangerDialog(
                    props.resources.get("SomethingWentWrong"),
                    props.resources.get("RefreshPageAndTryAgain")
                );
                return;
            }

            handleValidationErrors(result.error);

            return;
        }

        if (!!eventHandlers && !!eventHandlers.onSuccess) {
            eventHandlers.onSuccess();
            return;
        }

        const createdPermitId = result.value;
        const updatedUrl = permitPageUrl({
            permitType: props.permitType,
            departmentId: props.departmentId,
            documentReferenceId: createdPermitId
        });

        const storedPermitResult = await getPermitDocumentById<TPermitDocument>(createdPermitId, props.permitType);

        if (storedPermitResult.failed) {
            console.error(storedPermitResult.error);
            window.location.href = updatedUrl;

            return;
        }

        scrollToTop();

        window.history.replaceState(null, "", updatedUrl);

        setHasUnsavedChanges(false);
        setStoredPermit(storedPermitResult.value);
        setFormState(props.toFormState(storedPermitResult.value));
        showSuccessAlert(props.resources.get("WorkPermitCreated"));
    }

    async function submitUpdateHandlerAsync(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (props.updateAsync) {
            await props.updateAsync(formState);
            return;
        }

        await updatePermitHandlerAsync();
    }

    async function updatePermitHandlerAsync(options: UpdatePermitOptions = {}) {
        if (!storedPermit) {
            console.error("Attempted to update a permit with no stored permit in state.");
            return;
        }

        const permitRequest = props.toRequest(formState);

        if (!!options && options.clearSignaturesConfirmed) {
            (permitRequest as any).clearSignaturesConfirmed = true;
        }

        let updateResult: Result<void, ProblemDetails>;

        setRequestInProgress(true);
        updateResult = await updatePermitAsync(storedPermit.entityId, permitRequest, props.permitType);
        setRequestInProgress(false);

        if (updateResult.failed) {
            console.error(updateResult.error);

            setFormState((prevValue) => {
                try {
                    const updatedFormState = props.populateErrorMessages(prevValue, updateResult.error.errors);
                    return updatedFormState;
                } catch (error) {
                    console.error("Failed to populate error messages", error);
                    return prevValue;
                }
            });

            const errorLocations = getErrorLocations(updateResult.error, props.resources);
            showValidationErrorDialog(errorLocations, props.resources);

            return;
        }

        const refreshResult = await refreshStoredPermitAsync();

        setHasUnsavedChanges(false);

        scrollToTop();

        showSuccessAlert(props.resources.get("WorkPermitUpdated"));

        setFormState((prevValue) => {
            if (!refreshResult.failed) {
                const updatedFormState = props.toFormState(refreshResult.value);
                return updatedFormState;
            }

            const updatedFormState = props.clearErrorMessages(prevValue);
            return updatedFormState;
        });
    }

    function formKeyDownHandler(e: React.KeyboardEvent) {
        const submitButtonInFocus =
            !!document.activeElement &&
            document.activeElement.tagName === "BUTTON" &&
            document.activeElement.getAttribute("type") === "submit";

        const textAreaInFocus = !!document.activeElement && document.activeElement.tagName === "TEXTAREA";

        const isInputInFocus =
            !!document.activeElement &&
            document.activeElement.tagName === "INPUT" &&
            (document.activeElement as HTMLInputElement).type !== "submit";

        if (e.key === "Enter" && !submitButtonInFocus && !textAreaInFocus && !isInputInFocus) {
            e.preventDefault();
        }
    }

    async function createSignatureManagerAsync(
        signatureBlob: Blob,
        nameClarification: string,
        signatureType: SignatureType,
        phoneNumber?: string
    ) {
        if (!storedPermit) {
            console.error("Attempted to create a signature with no stored permit in state.");
            return;
        }

        const request: CreateSignatureRequest = {
            signatureBlob,
            nameClarification,
            signatureType,
            workPermitId: storedPermit.entityId
        };

        if (phoneNumber) {
            request.phoneNumber = phoneNumber;
        }

        const createResult: Result<number, string> = await createSignatureAsync(request);

        if (createResult.failed) {
            console.error(createResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("SaveSignatureError"));
            return;
        }

        await refreshStoredPermitAsync();
    }

    async function removeSignatureManagerAsync(signature: Signature) {
        if (!storedPermit || !signature) {
            console.error("Attempted to remove a signature with no stored permit or signature in state.");
            return;
        }

        const isBase64Signature = !signature.id && !!signature.base64Signature;

        const deleteResult = isBase64Signature
            ? await deleteBase64SignatureAsync(storedPermit.entityId, signature)
            : await deleteSignatureAsync(signature);

        if (deleteResult.failed) {
            console.error(deleteResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("DeleteSignatureError"));
            return;
        }

        await refreshStoredPermitAsync();
    }

    async function sendCounterSignatureRequestHandlerAsync(
        counterSignatureRequestRecipient: CounterSignatureRequestRecipient,
        counterSignatureLocation: CounterSignatureLocation
    ) {
        if (!storedPermit) {
            console.error("Attempted to send a counter signature request with no stored permit in state.");
            return;
        }

        const request: CreateCounterSignatureRequestPostRequest = {
            requestedCompanyId: counterSignatureRequestRecipient.companyId,
            counterSignatureLocation: counterSignatureLocation,
            requestedCompanyDepartmentId: counterSignatureRequestRecipient.companyDepartmentId,
            requestedUserId: counterSignatureRequestRecipient.userId,
            workPermitId: storedPermit.entityId
        };

        const sendResult: Result<number, ProblemDetails> = await sendCounterSignatureRequestAsync(request);

        if (sendResult.failed) {
            console.error(sendResult.error);
            showDangerDialog(
                props.resources.get("SomethingWentWrong"),
                props.resources.get("SendCounterSignatureRequestError")
            );
            return;
        }

        showSuccessAlert(props.resources.get("CounterSignatureRequestSent"));

        await refreshStoredPermitAsync();
    }

    async function cancelCounterSignatureRequestHandlerAsync(counterSignatureRequest: CounterSignatureRequest) {
        const cancelResult = await cancelCounterSignatureRequestAsync(counterSignatureRequest.id);

        if (cancelResult.failed) {
            console.error(cancelResult.error);
            showDangerDialog(
                props.resources.get("SomethingWentWrong"),
                props.resources.get("CancelCounterSignatureRequestError")
            );
            return;
        }

        showSuccessAlert(props.resources.get("CounterSignatureRequestCancelled"));

        await refreshStoredPermitAsync();
    }

    function getErrorLocations(problemDetails: ProblemDetails, resources: Resources): string[] {
        const errorLocations: string[] = [];

        const errorKeys = Object.keys(problemDetails.errors);

        errorKeys.forEach((errorKey) => {
            let errorResource: string | undefined = undefined;

            if (errorKey.includes("Positions")) {
                errorResource = "Positions";
            } else if (
                (errorKey.includes("StartDate") || errorKey.includes("EndDate")) &&
                !errorKey.includes("PermitAgreement")
            ) {
                errorResource = "WorkDuration";
            } else if (errorKey.includes("Entrepreneur")) {
                errorResource = "Entrepreneur";
            } else if (errorKey.includes("Description")) {
                errorResource = "WorkPurposeDescription";
            } else if (errorKey.includes("WorkLeader")) {
                errorResource = "WorkLeader";
            } else if (errorKey.includes("Persons")) {
                errorResource = "Participants";
            } else if (errorKey.includes("ProjectLeader")) {
                errorResource = "ProjectLeader";
            } else if (errorKey.includes("PermitAgreement")) {
                errorResource = "PermitAgreement";
            } else if (errorKey.includes("MethodOfWork")) {
                errorResource = "MethodOfWork";
            } else if (errorKey.includes("Measures")) {
                errorResource = "WorkTerms";
            } else if (errorKey.includes("PermitCategory")) {
                errorResource = "TypeOfWork";
            } else if (errorKey.includes("DivingChecklist")) {
                errorResource = "Diving";
            } else if (errorKey.includes("RaftChecklist")) {
                errorResource = "Raft";
            } else if (
                errorKey.includes("Measures") ||
                errorKey.includes("ProtectiveEquipment") ||
                errorKey.includes("RiskIdentifications")
            ) {
                errorResource = "WorkingConditions";
            } else if (errorKey.includes("HotWork")) {
                errorResource = "HotWorkTerms";
            } else if (errorKey.includes("OrderedBy")) {
                errorResource = "OrderedBy";
            }

            if (!errorResource) {
                return;
            }

            const locationResource = resources.get(errorResource);

            if (!locationResource) {
                console.error(`Could not find resource "${errorResource}"`);
                return;
            }

            errorLocations.push(locationResource);
        });

        const uniqueLocations = Array.from(new Set(errorLocations));

        return uniqueLocations;
    }

    async function unsavedChangesModalUpdateHandlerAsync() {
        setShowAcceptUnsavedChangesModal(false);
        await updatePermitHandlerAsync();
    }

    async function unsavedChangesModalAcceptHandlerAsync() {
        setShowAcceptUnsavedChangesModal(false);
        await acceptPermitHandlerAsync();
    }

    function registerFormStateChange(transformation: Transformation<TPermitForm>) {
        setHasUnsavedChanges(true);
        setFormState(transformation);
    }

    function copyButtonClickHandler() {
        if (!storedPermit) {
            console.error("Attempted to copy a permit with no stored permit in state.");
            return;
        }

        window.location.href = permitPageUrl({
            permitType: props.permitType,
            departmentId: props.departmentId,
            copyOfDocumentReferenceId: storedPermit.id
        });
    }

    function rejectPermitClickHandler() {
        if (!storedPermit) {
            console.error("Attempted to reject a permit with no stored permit in state.");
            return;
        }

        setShowRejectPermitModal(true);
    }

    async function rejectPermitHandlerAsync(rejectionComment: string) {
        if (!storedPermit) {
            console.error("Attempted to reject a permit with no stored permit in state.");
            return;
        }

        setShowRejectPermitModal(false);

        const rejectResult = await rejectPermitAsync(
            storedPermit.entityId,
            rejectionComment,
            PermitType.CoordinationPermit
        );

        if (rejectResult.failed) {
            console.error(rejectResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RefreshPageAndTryAgain"));
            return;
        }

        showSuccessAlert(props.resources.get("PermitRejected"));
        await refreshStoredPermitAsync();
    }

    function handleValidationErrors(error: ProblemDetails) {
        console.error(error);

        if (!error.errors) {
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RefreshPageAndTryAgain"));
            return;
        }

        setFormState((prevValue) => {
            try {
                const updatedFormState = props.populateErrorMessages(prevValue, error.errors);
                return updatedFormState;
            } catch (error) {
                console.error("Failed to populate error messages", error);
                return prevValue;
            }
        });
        const errorLocations = getErrorLocations(error, props.resources);
        showValidationErrorDialog(errorLocations, props.resources);
    }

    async function renewPermitClickHandlerAsync() {
        if (!storedPermit) {
            console.error("Attempted to renew a permit with no stored permit in state.");
            return;
        }

        const renewResult = await renewPermitAsync(storedPermit.entityId, props.permitType);

        if (renewResult.failed) {
            console.error(renewResult.error);
            showDangerDialog(props.resources.get("SomethingWentWrong"), props.resources.get("RenewError"));
            return;
        }

        await refreshStoredPermitAsync();
        showSuccessAlert(props.resources.get("PermitRenewed"));
    }

    const acceptButtonClickHandler = hasUnsavedChanges
        ? () => setShowAcceptUnsavedChangesModal(true)
        : acceptPermitHandlerAsync;

    const isNewPermit = !storedPermit;
    const canCreate = isNewPermit;
    const canUpdate = !!storedPermit && storedPermit.canUpdate;
    const canManageContent = isNewPermit || canUpdate;

    const canManagePositionPolygons =
        (!!isNewPermit &&
            props.baseUserInfo.roles.some(
                (r) =>
                    (r.departmentId === props.departmentId &&
                        (r.roleCode === CodeRole.Admin ||
                            r.roleCode === CodeRole.InternalCaseManager ||
                            r.roleCode === CodeRole.InternalDirectorFlammableProduct)) ||
                    r.roleCode === CodeRole.SystemOwner
            )) ||
        (!!storedPermit && storedPermit.canManagePositionPolygons);

    const contentVersion = storedPermit?.contentVersion ?? Math.max(...Object.values(permitContentVersions));

    return {
        canCreate,
        canUpdate,

        storedPermit,
        department,
        isLoading,
        formState,
        isNewPermit,
        hasUnsavedChanges,
        showAcceptUnsavedChangesModal,
        requestInProgress,
        showRejectPermitModal,
        canManageContent,
        canManagePositionPolygons,
        contentVersion,

        setFormState,
        refreshStoredPermitAsync,
        acceptPermitHandlerAsync,
        submitCreateHandlerAsync,
        updatePermitHandlerAsync,
        addPositionPickerRowHandler,
        removePositionPickerRowHandler,
        updatePositionPickerRowHandler,
        formStateChangeHandler,
        addParticipantHandler,
        removeParticipantHandler,
        updateParticipantHandler,
        personFormElementChangeHandler,
        formKeyDownHandler,
        createSignatureManagerAsync,
        removeSignatureManagerAsync,
        sendCounterSignatureRequestHandlerAsync,
        cancelCounterSignatureRequestHandlerAsync,
        getErrorLocations,
        setHasUnsavedChanges,
        setShowAcceptUnsavedChangesModal,
        submitUpdateHandlerAsync,
        setRequestInProgress,
        unsavedChangesModalUpdateHandlerAsync,
        unsavedChangesModalAcceptHandlerAsync,
        setStoredPermit,
        setDepartment,
        registerFormStateChange,
        copyButtonClickHandler,
        acceptButtonClickHandler,
        rejectPermitHandlerAsync,
        rejectPermitClickHandler,
        setShowRejectPermitModal,
        handleValidationErrors,
        createHandlerAsync,
        renewPermitAsync,
        renewPermitClickHandlerAsync
    };
}

export default usePermitForm;

type UpdatePermitOptions = {
    clearSignaturesConfirmed?: boolean;
};
